'use client'
import type {
    HotelDestinationKind,
    HotelDestinationPayload,
    HotelRoomConfig,
    HotelSearchInitialValues
} from '@/src/components/common/home/hotelSearch/searchForm/hooks/useHotelSearchManager'
import { HOTEL_LAST_SEARCH_STORAGE_KEY } from '@/src/constants/hotels'
import { getFormattedDate } from '@/src/utils/date'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

/** Serialized form of a saved search — dates as "YYYY-MM-DD", everything else as-is. */
interface StoredHotelSearch {
    destination: string
    destinationPayload: HotelDestinationPayload
    destinationKind: HotelDestinationKind
    checkIn: string
    checkOut: string
    roomConfigs: HotelRoomConfig[]
}

interface SaveHotelLastSearchParams {
    destination: string
    destinationPayload: HotelDestinationPayload
    destinationKind: HotelDestinationKind
    checkIn: Date
    checkOut: Date
    roomConfigs: HotelRoomConfig[]
}

/** "2026-09-10" → Date, or null when absent/unparseable. */
const parseIsoDate = (iso: string): Date | null => {
    if (!iso) return null
    const [year, month, day] = iso.split('-').map(Number)
    if (!year || !month || !day) return null
    const date = new Date(year, month - 1, day)
    return isNaN(date.getTime()) ? null : date
}

const isFutureDate = (date: Date | null): boolean => {
    if (!date) return false
    return dayjs(date).startOf('day').isAfter(dayjs().startOf('day').subtract(1, 'day'))
}

// Persist the just-run search so the home form can replay it. Called only with a
// complete, valid search (the SRP path was successfully built), so no partial state.
export const saveHotelLastSearch = (params: SaveHotelLastSearchParams): void => {
    if (typeof window === 'undefined') return
    const stored: StoredHotelSearch = {
        destination: params.destination,
        destinationPayload: params.destinationPayload,
        destinationKind: params.destinationKind,
        checkIn: getFormattedDate(params.checkIn, 'YYYY-MM-DD'),
        checkOut: getFormattedDate(params.checkOut, 'YYYY-MM-DD'),
        roomConfigs: params.roomConfigs
    }
    try {
        window.localStorage.setItem(HOTEL_LAST_SEARCH_STORAGE_KEY, JSON.stringify(stored))
    } catch {
        // Storage full or blocked (private mode): prefill is a nicety, not worth surfacing.
    }
}

// Read the saved search back into form-seed values. Past-dated check-in/check-out are
// dropped (the date picker rejects them) while destination and guests are kept, so a
// returning user only re-picks dates.
export const readHotelLastSearch = (): HotelSearchInitialValues | undefined => {
    if (typeof window === 'undefined') return undefined

    let raw: string | null
    try {
        raw = window.localStorage.getItem(HOTEL_LAST_SEARCH_STORAGE_KEY)
    } catch {
        return undefined
    }
    if (!raw) return undefined

    let stored: StoredHotelSearch
    try {
        stored = JSON.parse(raw)
    } catch {
        return undefined
    }

    if (!stored?.destinationPayload || !stored.destinationKind || !Array.isArray(stored.roomConfigs)) {
        return undefined
    }

    const checkIn = parseIsoDate(stored.checkIn)
    const checkOut = parseIsoDate(stored.checkOut)
    const datesUsable = isFutureDate(checkIn) && isFutureDate(checkOut)

    return {
        destination: stored.destination,
        destinationPayload: stored.destinationPayload,
        destinationKind: stored.destinationKind,
        checkIn: datesUsable ? checkIn : null,
        checkOut: datesUsable ? checkOut : null,
        roomConfigs: stored.roomConfigs
    }
}

/**
 * Reads the last search from localStorage after mount. `ready` flips false→true once,
 * letting the caller remount the form with a key so SSR/first client render stay empty
 * (no hydration mismatch) and the seed applies on the second render.
 */
export const useHotelLastSearchInitialValues = (): { initialValues?: HotelSearchInitialValues; ready: boolean } => {
    const [state, setState] = useState<{ initialValues?: HotelSearchInitialValues; ready: boolean }>({ ready: false })

    useEffect(() => {
        setState({ initialValues: readHotelLastSearch(), ready: true })
    }, [])

    return state
}
